import React, { useState, useEffect, useCallback } from "react";
import { MessageCircle, BadgeCheck } from "lucide-react";
import { useAuth } from "../AuthContext";
import { API_URL } from "../config";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("ru", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function CommentSection({ contentType, contentId, onLoginRequest }) {
  const { isLoggedIn, isEditor, user, token } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/comments?content_type=${contentType}&content_id=${contentId}`)
      .then(r => r.ok ? r.json() : [])
      .then(setComments)
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [contentType, contentId]);

  useEffect(() => { load(); }, [load]);

  const send = async e => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    setSending(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content_type: contentType, content_id: contentId, text: value }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Не удалось отправить комментарий");
      setComments(prev => [...prev, data]);
      setText("");
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const remove = async id => {
    if (!window.confirm("Удалить комментарий?")) return;
    const r = await fetch(`${API_URL}/comments/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) setComments(prev => prev.filter(c => c.id !== id));
  };

  const report = async id => {
    if (!isLoggedIn) { onLoginRequest && onLoginRequest(); return; }
    const reason = window.prompt("Опишите причину жалобы (необязательно):") || "";
    await fetch(`${API_URL}/reports`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content_type: "comment", content_id: id, reason }),
    });
    window.alert("Жалоба отправлена администратору.");
  };

  return (
    <div style={{ marginTop: 28, borderTop: "1.5px solid #eaecf4", paddingTop: 18 }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "1.1rem", color: "#007c84", marginBottom: 14 }}>
        <MessageCircle size={18} strokeWidth={2.2} /> Комментарии {comments.length > 0 && `(${comments.length})`}
      </h3>

      {loading && <p style={{ color: "#888", fontSize: "0.9rem" }}>Загрузка…</p>}

      {!loading && comments.length === 0 && (
        <p style={{ color: "#999", fontStyle: "italic", fontSize: "0.9rem" }}>Комментариев пока нет.</p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
        {comments.map(c => (
          <div key={c.id} style={{ background: "#f8fafd", border: "1px solid #e8ecf4", borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 700, color: "#007c84", fontSize: "0.9rem" }}>
                {c.author_name || "Бывший пользователь"}
              </span>
              {c.author_verified && (
                <span title="Подтверждённый врач" style={{ color: "#15803d", display: "inline-flex" }}>
                  <BadgeCheck size={15} strokeWidth={2.2} />
                </span>
              )}
              <span style={{ fontSize: "0.78rem", color: "#aaa" }}>{fmtDate(c.created_at)}</span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
                {isLoggedIn && (!user || c.author_id !== user.id) && (
                  <button onClick={() => report(c.id)}
                    style={{ background: "none", border: "none", color: "#888", fontSize: "0.78rem", cursor: "pointer", padding: 0 }}>
                    ⚠ пожаловаться
                  </button>
                )}
                {(isEditor || (user && c.author_id === user.id)) && (
                  <button onClick={() => remove(c.id)}
                    style={{ background: "none", border: "none", color: "#c0392b", fontSize: "0.78rem", cursor: "pointer", padding: 0 }}>
                    ✕ удалить
                  </button>
                )}
              </span>
            </div>
            <div style={{ fontSize: "0.95rem", color: "#333", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{c.text}</div>
          </div>
        ))}
      </div>

      {isLoggedIn ? (
        <form onSubmit={send} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {error && <div className="auth-error">{error}</div>}
          <textarea
            className="admin-textarea" rows={3} value={text}
            onChange={e => setText(e.target.value.slice(0, 2000))}
            placeholder="Ваш комментарий…"
          />
          <button type="submit" disabled={sending || !text.trim()} style={{ alignSelf: "flex-start", marginTop: 0 }}>
            {sending ? "Отправка…" : "Отправить"}
          </button>
        </form>
      ) : (
        <p style={{ color: "#666", fontSize: "0.92rem" }}>
          Чтобы оставить комментарий —{" "}
          <button onClick={onLoginRequest} className="btn-link">войдите или зарегистрируйтесь</button>.
        </p>
      )}
    </div>
  );
}
